import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useTrackingEnabled from '../../hooks/useTrackingEnabled';
import { isTrackingHost, syncTikTokConsent } from '../consent/consentStore';

const TIKTOK_PIXEL_ID = 'D3P1VPJC77U1STIOKMSG';

/**
 * Global TikTok pixel, respecting admin status.
 * This component should be rendered inside AuthProvider.
 *
 * Meta is not handled here: the only Meta pixel left is the /apply funnel one
 * (see utils/pixel/applyFunnelPixel), which owns its own lifecycle.
 */
const TrackingPixels: React.FC = () => {
  const isTrackingEnabled = useTrackingEnabled();
  const location = useLocation();

  // Initialize TikTok Pixel
  useEffect(() => {
    // Production only: preprod traffic has no business in the ad account.
    if (!isTrackingEnabled || !isTrackingHost()) return;

    if (typeof window !== 'undefined' && !window.ttq) {
      (function (w: any, d: Document, t: string) {
        w.TiktokAnalyticsObject = t;
        const ttq = (w[t] = w[t] || []);
        ttq.methods = [
          'page',
          'track',
          'identify',
          'instances',
          'debug',
          'on',
          'off',
          'once',
          'ready',
          'alias',
          'group',
          'enableCookie',
          'disableCookie',
          'holdConsent',
          'revokeConsent',
          'grantConsent',
        ];
        ttq.setAndDefer = function (obj: any, method: string) {
          obj[method] = function () {
            obj.push([method].concat(Array.prototype.slice.call(arguments, 0)));
          };
        };
        for (let i = 0; i < ttq.methods.length; i++) {
          ttq.setAndDefer(ttq, ttq.methods[i]);
        }
        ttq.instance = function (name: string) {
          const inst = ttq._i[name] || [];
          for (let i = 0; i < ttq.methods.length; i++) {
            ttq.setAndDefer(inst, ttq.methods[i]);
          }
          return inst;
        };
        ttq.load = function (id: string, opts?: Record<string, any>) {
          const src = 'https://analytics.tiktok.com/i18n/pixel/events.js';
          ttq._i = ttq._i || {};
          ttq._i[id] = [];
          ttq._i[id]._u = src;
          ttq._t = ttq._t || {};
          ttq._t[id] = +new Date();
          ttq._o = ttq._o || {};
          ttq._o[id] = opts || {};

          const s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = `${src}?sdkid=${id}&lib=${t}`;
          const firstScript = d.getElementsByTagName('script')[0];
          firstScript.parentNode?.insertBefore(s, firstScript);
        };

        ttq.load(TIKTOK_PIXEL_ID);
        // Muted by default; CookieBanner decides. Held before any page() call so a
        // visitor who has not accepted yet is never measured.
        ttq.holdConsent();
      })(window, document, 'ttq');
    }

    syncTikTokConsent();
  }, [isTrackingEnabled]);

  // Track page views on route change
  useEffect(() => {
    if (!isTrackingEnabled || !isTrackingHost()) return;

    window.ttq?.page();
  }, [location.pathname, location.search, isTrackingEnabled]);

  return null;
};

export default TrackingPixels;
